import {Timestamp} from 'firebase/firestore';
import {Activity, HubblTask, Palette, User} from 'hubbl-shared';
import {memo} from 'react';
import styled from 'styled-components';
import {mapUserToUserDTO} from '../../api/utils/map-incoming-users/mapUserToUserDTO';
import {useI18n} from '../../hooks';
import {useAppSelector} from '../../hooks/store/useAppSelector';
import {HubblBreakDTO, HubblGroupDTO} from '../../models';
import {CompanySelector} from '../../store/company/CompanySelector';
import {GlobalSelector} from '../../store/global/GlobalSelector';
import {
  PrimaryButton,
  SecondaryButton,
  SelectedFilterPills,
  TextBodyMedium,
  TextHeading3,
  TextHeading6,
  TextHeading7,
} from '../index';
import {HubblConnectCard} from '../social-activity-cards/HubblConnectCard';
import {HubblGroupCard} from '../social-activity-cards/HubblGroupCard';

interface ActivityConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type: 'connect' | 'group';
  activityData: {
    // Connect data
    activities?: Activity[];
    startDateTime: string;
    endDateTime?: string;
    // Group data
    title?: string;
    description?: string;
    emoji?: string;
    location?: string;
    extraDetails?: string;
    maxParticipants?: number;
    // Common data
    invitedUsers: User[];
    filters: any[];
    isRepeatEnabled: boolean;
    tasks?: HubblTask[];
  };
  currentUser: User;
  isLoading?: boolean;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalWrapper = styled.div`
  width: 95%;
  max-width: 1000px;
  max-height: 90vh;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 32px 40px;
  border-bottom: 1px solid ${Palette.almostBlackTransparent10};
  background: ${Palette.primarySubtleGreen};
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px 40px;
`;

const ModalFooter = styled.div`
  padding: 32px 40px;
  border-top: 1px solid ${Palette.almostBlackTransparent10};
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  background: ${Palette.white};
`;

const PreviewContainer = styled.div`
  margin: 24px 0;
  display: flex;
  justify-content: center;
`;

const CardWrapper = styled.div`
  width: 100%;
  pointer-events: none; /* Disable interactions in preview */
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  padding-right: 24px;

  /* Override card hover effects for preview */
  * {
    cursor: default !important;
  }
`;

const DetailsContainer = styled.div`
  background: ${Palette.primarySubtleGreen};
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${Palette.almostBlackTransparent10};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled(TextBodyMedium)`
  color: ${Palette.almostBlack750};
  font-weight: 500;
`;

const DetailValue = styled(TextBodyMedium)`
  color: ${Palette.almostBlack};
  text-align: right;
  flex: 1;
  margin-left: 16px;
`;

const InvitedUsersContainer = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const ListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  flex: 1;
  margin-left: 16px;
`;

const UserTag = styled.div`
  background: ${Palette.primaryCherokee};
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
`;

const RepeatInfo = styled.div`
  background: ${Palette.primaryTerraCotta}20;
  color: ${Palette.primaryTerraCotta};
  padding: 12px;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const PreviewOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  border-radius: 16px;
  pointer-events: none;
  z-index: 10;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 12px;
`;

const PreviewBadge = styled.div`
  background: ${Palette.primaryTerraCotta};
  color: ${Palette.white};
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const TaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-left: 12px;
  padding-right: 12px;
  padding-top: 8px;
  padding-bottom: 8px;
  background: ${Palette.white};
  border-radius: 24px;
`;

const TaskTitle = styled.div`
  flex: 1;
  font-weight: 500;
  color: ${Palette.almostBlack};
  font-size: 14px;
`;

const TaskStatus = styled.div`
  font-size: 12px;
  color: ${Palette.almostBlack500};
  font-weight: 400;
  flex-shrink: 0;
`;

export const ActivityConfirmationModal = memo(
  ({
    isOpen,
    onClose,
    onConfirm,
    type,
    activityData,
    currentUser,

    isLoading = false,
  }: ActivityConfirmationModalProps) => {
    const t = useI18n();

    const company = useAppSelector(CompanySelector.getCompanyDetails);
    const prefLanguage = useAppSelector(GlobalSelector.getPreferredLanguage);
    // Create preview data for the cards
    const createPreviewData = () => {
      const startTime = new Date(activityData.startDateTime);
      const endTime = activityData.endDateTime
        ? new Date(activityData.endDateTime)
        : null;
      const currentUserDTO = mapUserToUserDTO(currentUser);
      const invitedUsers =
        activityData.invitedUsers.length > 0
          ? [currentUserDTO].map(user => ({
              id: user.id,
              fullName: user.fullName,
              firstName: user.firstName,
              photoUrl: user.photoUrl,
              department: user.department,
              email: user.email,
            }))
          : [];

      if (type === 'connect') {
        const connectData: HubblBreakDTO = {
          id: 'preview',
          activities: activityData.activities || [],
          startDateTime: Timestamp.fromDate(startTime),
          endDateTime: endTime ? Timestamp.fromDate(endTime) : null,
          created: Timestamp.now(),
          userCreator: currentUserDTO,
          userAcceptor: null,
          declinedUserIds: [],
          declinedUsers: [],
          tasks: [],
          invitedUsers,
          filters: activityData.filters,
          isRepeatEnabled: activityData.isRepeatEnabled,
          isRepeatedHubbl: false,
          companyId: currentUserDTO.companyId,
          messages: [],
          userIds: [currentUserDTO.id],
          isBreakPassed: false,
          officeId: 'default',
          spaceId: 'default',
        };
        return connectData;
      } else {
        const groupData: HubblGroupDTO = {
          id: 'preview',
          type: 'group',
          description: {
            en: activityData.title || '',
            da: activityData.title || '',
          },
          emoji: activityData.emoji || '🎉',
          startDateTime: Timestamp.fromDate(startTime),
          endDateTime: endTime ? Timestamp.fromDate(endTime) : null,
          createdDateTime: Timestamp.now(),
          creator: currentUserDTO,
          attendees: [currentUserDTO],
          attendeeIds: [currentUserDTO.id],
          maxAttendees: activityData.maxParticipants || null,
          location: activityData.location || null,
          tasks: [],
          extraDetails: activityData.extraDetails
            ? {en: activityData.extraDetails, da: activityData.extraDetails}
            : null,
          color: Palette.primaryPadua,
          isGroupFilled: false,
          labels: [],
          companyId: currentUser.companyId,
          isHubblPassed: false,
          isRepeatEnabled: activityData.isRepeatEnabled,
          isRepeatedHubbl: false,
          messages: [],
          invitedUsers: invitedUsers,
          declinedUserIds: [],
          declinedUsers: [],
          filters: activityData.filters,
          officeId: 'default',
          spaceId: 'default',
        };
        return groupData;
      }
    };

    const previewData = createPreviewData();

    const formatDateTime = (date: Date) => {
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    };

    if (!isOpen) return null;

    return (
      <ModalOverlay onClick={onClose}>
        <ModalWrapper onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <TextHeading3
              text={`${t.modals.confirm} ${type === 'connect' ? t.connects.oneOnOneConnect : t.activities.groupActivity}`}
            />
          </ModalHeader>

          <ModalContent>
            <TextHeading6
              text={t.events.previewDescription}
              color={Palette.almostBlack750}
            />

            <PreviewContainer>
              <CardWrapper>
                {type === 'connect' ? (
                  <HubblConnectCard
                    hubblConnect={previewData as HubblBreakDTO}
                    isPast={false}
                  />
                ) : (
                  <HubblGroupCard
                    hubblGroup={previewData as HubblGroupDTO}
                    isPast={false}
                  />
                )}
                <PreviewOverlay>
                  <PreviewBadge>{t.events.preview}</PreviewBadge>
                </PreviewOverlay>
              </CardWrapper>
            </PreviewContainer>

            <DetailsContainer>
              <TextHeading6 text={t.events.activityDetails} />

              <DetailRow>
                <DetailLabel text={t.events.type} />
                <DetailValue
                  text={
                    type === 'connect'
                      ? t.connects.oneOnOneConnect
                      : t.activities.groupActivity
                  }
                />
              </DetailRow>

              <DetailRow>
                <DetailLabel text={t.common.title} />
                <DetailValue
                  text={(() => {
                    const emoji =
                      type === 'connect'
                        ? activityData.activities?.[0]?.emoji || '😊'
                        : activityData.emoji || '🎉';
                    const title =
                      type === 'connect'
                        ? activityData.activities?.[0]?.label[prefLanguage] ||
                          t.activities.customActivity
                        : activityData.title || t.activities.groupActivity;
                    const fullTitle = `${emoji} ${title}`;
                    return fullTitle.length > 90
                      ? fullTitle.substring(0, 87) + '...'
                      : fullTitle;
                  })()}
                />
              </DetailRow>

              <DetailRow>
                <DetailLabel text={t.events.startTime} />
                <DetailValue
                  text={formatDateTime(new Date(activityData.startDateTime))}
                />
              </DetailRow>

              {activityData.endDateTime && (
                <DetailRow>
                  <DetailLabel text={t.events.endTime} />
                  <DetailValue
                    text={formatDateTime(new Date(activityData.endDateTime))}
                  />
                </DetailRow>
              )}

              {type === 'group' && (
                <DetailRow>
                  <DetailLabel text={t.events.maxParticipants} />
                  <DetailValue
                    text={
                      activityData.maxParticipants &&
                      activityData.maxParticipants > 0
                        ? activityData.maxParticipants.toString()
                        : t.events.unlimited
                    }
                  />
                </DetailRow>
              )}

              {type === 'group' && activityData.location && (
                <DetailRow>
                  <DetailLabel text={t.common.location} />
                  <DetailValue text={activityData.location} />
                </DetailRow>
              )}

              {type === 'group' && activityData.extraDetails && (
                <DetailRow>
                  <DetailLabel text={t.events.additionalDetails} />
                  <DetailValue text={activityData.extraDetails} />
                </DetailRow>
              )}

              {activityData.filters && activityData.filters.length > 0 && (
                <DetailRow>
                  <DetailLabel text={t.events.visibilityFilters} />
                  <div style={{marginTop: '8px'}}>
                    <SelectedFilterPills
                      filters={activityData.filters}
                      orgMetadata={company?.organizationalMetadata}
                      maxWidth={600}
                    />
                  </div>
                </DetailRow>
              )}

              {activityData.invitedUsers.length > 0 && (
                <InvitedUsersContainer>
                  <DetailLabel text={t.events.invitedUsers} />
                  <ListContainer>
                    {activityData.invitedUsers.map(user => (
                      <UserTag key={user.id}>
                        <TextHeading7 text={user.fullName} />
                      </UserTag>
                    ))}
                  </ListContainer>
                </InvitedUsersContainer>
              )}

              {activityData.tasks && activityData.tasks.length > 0 && (
                <DetailRow>
                  <DetailLabel text={t.common.tasks} />
                  <ListContainer>
                    {activityData.tasks.map((task, index) => (
                      <TaskItem key={task.id || index}>
                        <TaskTitle>{task.title}</TaskTitle>
                        <TaskStatus>
                          {task.assignedUserId
                            ? `◦ ${t.events.willBeAssignedLater}`
                            : `◦ ${t.events.unassigned}`}
                        </TaskStatus>
                      </TaskItem>
                    ))}
                  </ListContainer>
                </DetailRow>
              )}

              <DetailRow>
                <DetailLabel text={t.events.recurring} />
                <DetailValue
                  text={
                    activityData.isRepeatEnabled ? t.common.yes : t.common.no
                  }
                />
              </DetailRow>

              {activityData.isRepeatEnabled && (
                <RepeatInfo>🔄 {t.events.willBeRecurringActivity}</RepeatInfo>
              )}
            </DetailsContainer>
          </ModalContent>

          <ModalFooter>
            <SecondaryButton
              label={t.common.back}
              onClick={onClose}
              isDisabled={isLoading}
            />
            <PrimaryButton
              label={
                isLoading
                  ? t.common.creating
                  : type === 'connect'
                    ? t.events.createConnection
                    : t.events.createActivity
              }
              onClick={onConfirm}
              isDisabled={isLoading}
            />
          </ModalFooter>
        </ModalWrapper>
      </ModalOverlay>
    );
  },
);
